'use client';

import { useState, useEffect } from 'react';
import { X, ArrowRight, Sparkles, MessageCircle, Zap } from 'lucide-react';
import { trackEvent } from './analytics';

export interface OnboardingStep {
  title: string;
  description: string;
  icon: React.ReactNode;
  highlight?: string;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    title: 'Welcome to DreamBot',
    description: 'Your AI sleep concierge that helps you find the perfect mattress based on your unique needs.',
    icon: <Sparkles size={32} className="text-foreground" />,
  },
  {
    title: 'Quick & Personalized',
    description: 'Answer a few simple questions about your sleep position, comfort preferences, and budget.',
    icon: <MessageCircle size={32} className="text-foreground" />,
  },
  {
    title: 'Smart Recommendations',
    description: 'Get AI-powered product recommendations tailored specifically to you, with detailed explanations.',
    icon: <Zap size={32} className="text-foreground" />,
  },
];

const STORAGE_KEY = 'dreambot_onboarding_completed';

export function hasCompletedOnboarding(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function markOnboardingComplete(): void {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    // Silently fail
  }
}

export function resetOnboarding(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently fail
  }
}

export interface OnboardingProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function Onboarding({ onComplete, onSkip }: OnboardingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;
  const step = ONBOARDING_STEPS[currentStep];

  useEffect(() => {
    trackEvent('onboarding_started', { step: 0 });
  }, []);

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep((prev) => prev + 1);
      trackEvent('onboarding_step_completed', { step: currentStep });
    }
  };

  const handleComplete = () => {
    setIsExiting(true);
    markOnboardingComplete();
    trackEvent('onboarding_completed', { steps_viewed: currentStep + 1 });
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  const handleSkip = () => {
    setIsExiting(true);
    markOnboardingComplete();
    trackEvent('onboarding_skipped', { at_step: currentStep });
    setTimeout(() => {
      onSkip();
    }, 300);
  };

  return (
    <div
      className={`
        absolute inset-0 z-50 bg-card flex flex-col
        transition-opacity duration-300
        ${isExiting ? 'opacity-0' : 'opacity-100'}
      `}
    >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-border/50 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
              {step.icon}
            </div>
            <h2 className="text-[17px] leading-[1.2] tracking-[-0.02em] font-semibold text-foreground">Tutorial</h2>
          </div>
          <button
            onClick={handleSkip}
            aria-label="Skip tutorial"
            className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors"
          >
            <X size={16} className="text-foreground" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-5 py-6 flex flex-col items-center justify-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
              {step.icon}
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-8 max-w-md">
            <h3 className="text-2xl font-bold text-foreground mb-3 tracking-tight">
              {step.title}
            </h3>
            <p className="text-muted-foreground leading-relaxed text-[15px]">
              {step.description}
            </p>
          </div>

          {/* Progress indicators */}
          <div className="flex justify-center gap-2 mb-6">
            {ONBOARDING_STEPS.map((_, index) => (
              <div
                key={index}
                className={`
                  h-1.5 rounded-full transition-all duration-300
                  ${index === currentStep ? 'w-8 bg-foreground' : 'w-1.5 bg-muted'}
                `}
              />
            ))}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-3 border-t border-border/50 shrink-0">
          <div className="flex gap-3">
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep((prev) => prev - 1)}
                className="flex-1 px-6 py-2.5 bg-muted text-foreground rounded-xl font-semibold text-[14px] tracking-[-0.01em] transition-all hover:opacity-90 active:scale-[0.98]"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="flex-1 px-6 py-2.5 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold text-[14px] tracking-[-0.01em] transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              <ArrowRight size={16} />
            </button>
          </div>

          {/* Skip text */}
          {!isLastStep && (
            <button
              onClick={handleSkip}
              className="w-full mt-3 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
            >
              Skip tutorial
            </button>
          )}
        </div>
    </div>
  );
}
